import * as React from "react"
import { useTranslation } from "react-i18next"

import { NutrientWeightInput } from "Catalog/shared/components/DimensionInput"
import StatefulField from "shared/components/StatefulField"
import { IPer100Values, Per100Per } from "shared/hubrise/models"

import EnergySelection from "./EnergySelection"
import { Hint, Label, Row, Rows, RowSeparator, Section, SectionTitle } from "./Styles"

interface ValuesProps {
  per100Values: IPer100Values | null
  onChange: (value: IPer100Values | null) => void
}

const EMPTY_PER_100_VALUES: IPer100Values = {
  per: "100 g",
  energy: null,
  fat: null,
  saturated_fat: null,
  mono_unsaturated_fat: null,
  poly_unsaturated_fat: null,
  carbohydrate: null,
  sugar: null,
  polyols: null,
  starch: null,
  fibre: null,
  protein: null,
  salt: null,
}

type NutrientField = Exclude<keyof IPer100Values, "per" | "energy">

const NUTRIENT_FIELDS: Array<{ field: NutrientField; indent: boolean }> = [
  { field: "fat", indent: false },
  { field: "saturated_fat", indent: true },
  { field: "mono_unsaturated_fat", indent: true },
  { field: "poly_unsaturated_fat", indent: true },
  { field: "carbohydrate", indent: false },
  { field: "sugar", indent: true },
  { field: "polyols", indent: true },
  { field: "starch", indent: true },
  { field: "fibre", indent: false },
  { field: "protein", indent: false },
  { field: "salt", indent: false },
]

const Values = ({ per100Values, onChange }: ValuesProps): JSX.Element => {
  const { t } = useTranslation()

  const values = per100Values ?? EMPTY_PER_100_VALUES

  const update = (patch: Partial<IPer100Values>) => {
    const newValues = { ...values, ...patch }
    const isNull = Object.entries(newValues).every(([key, value]) =>
      key === "per" ? value === "100 g" : value === null,
    )
    onChange(isNull ? null : newValues)
  }

  const perKey = values.per === "100 ml" ? "per_100ml" : "per_100g"

  return (
    <>
      <StatefulField.Select
        initialValue={[values.per]}
        label={t("nutrition.component.values.per")}
        withMargin
        options={[
          { label: t("nutrition.component.values.per_100g"), value: "100 g" },
          { label: t("nutrition.component.values.per_100ml"), value: "100 ml" },
        ]}
        onChange={(vals) => update({ per: vals[0] as Per100Per })}
        searchable={false}
        debounce={false}
      />

      <Section>
        <SectionTitle>{t(`nutrition.component.values.energy.title.${perKey}`)}</SectionTitle>

        <EnergySelection initialValue={values.energy} onChange={(energy) => update({ energy })} />
      </Section>

      <Section>
        <SectionTitle>{t(`nutrition.component.values.nutrients.${perKey}`)}</SectionTitle>

        <Rows>
          {NUTRIENT_FIELDS.map(({ field, indent }, index) => (
            <React.Fragment key={field}>
              {index > 0 && !indent ? <RowSeparator /> : null}

              <Row>
                <Label $indent={indent}>{t(`nutrition.nutrients.${field}`)}</Label>

                <NutrientWeightInput
                  initialValue={values[field]}
                  placeholder="0 g"
                  defaultUnit="g"
                  onChange={(val) => update({ [field]: val })}
                />
              </Row>
            </React.Fragment>
          ))}
        </Rows>

        <Hint>{t("nutrition.component.values.supported_units")}</Hint>
      </Section>
    </>
  )
}

export default Values
